/**
 * Sector operations — Unified Sector Registry.
 * R39 G1 (plan docs/plans/sector-operations.md §4, §5 SO-P101).
 */

export const OPS_SECTOR_KEYS = [
  'construction',
  'hospital',
  'sites',
  'delivery',
  'shops',
  'stays',
  'jobs',
  'clinics',
  'plant',
  'fitness',
] as const
export type OpsSector = (typeof OPS_SECTOR_KEYS)[number]

export const SITES_PRESETS = ['GUARDING', 'FACILITY'] as const
export type SitesPreset = (typeof SITES_PRESETS)[number]

export const SHOPS_PRESETS = [
  'GENERAL',
  'APPAREL_ELECTRONICS',
  'FOOD',
  'PHARMACY_OPS',
  'RESTAURANT',
] as const
export type ShopsPreset = (typeof SHOPS_PRESETS)[number]

export const STAYS_PRESETS = [
  'BUDGET_HOTEL',
  'BUSINESS_HOTEL',
  'RESORT_HERITAGE',
  'HOMESTAY_VILLA',
  'SERVICED_APARTMENT',
  'HOSTEL',
] as const
export type StaysPreset = (typeof STAYS_PRESETS)[number]

export const CLINICS_SITE_KINDS = [
  'CLINIC',
  'LAB',
  'HUB',
  'COLLECTION_CENTRE',
  'PHARMACY',
  'WAREHOUSE',
  'CAMP',
] as const
export type ClinicsSiteKind = (typeof CLINICS_SITE_KINDS)[number]

export const CLINICS_PRESET_FAMILIES = [
  'CONSULT',
  'PROCEDURE',
  'LABS',
  'IMAGING',
  'BLOOD_BANK',
  'PHARMACY',
  'VET',
] as const
export type ClinicsPresetFamily = (typeof CLINICS_PRESET_FAMILIES)[number]

export const CLINICS_PRESETS = [
  'SINGLE_DOCTOR_FAMILY',
  'POLYCLINIC',
  'MATERNITY_CHILD',
  'MENTAL_HEALTH',
  'AYUSH_WELLNESS',
  'VACCINATION',
  'DENTAL',
  'EYE_OPTICAL',
  'SKIN_COSMETIC_HAIR',
  'IVF',
  'PHYSIO_REHAB',
  'DIALYSIS',
  'DAY_CARE_NURSING_HOME',
  'PATHOLOGY_LAB',
  'COLLECTION_CENTRE',
  'HOME_COLLECTION',
  'IMAGING_CENTRE',
  'BLOOD_BANK',
  'PHARMACY_CHAIN',
  'PHARMA_DEVICE_DISTRIBUTOR',
  'VET_CLINIC',
] as const
export type ClinicsPreset = (typeof CLINICS_PRESETS)[number]

export const PLANT_PRESETS = [
  'GARMENTS_TEXTILES',
  'FOOD_PROCESSING',
  'ENGINEERING_METAL',
  'GENERIC_MSME',
] as const
export type PlantPreset = (typeof PLANT_PRESETS)[number]

export const FITNESS_PRESET_GROUPS = [
  'GYMS_STUDIOS',
  'WATER_SPORT',
  'PROGRAMMES',
] as const
export type FitnessPresetGroup = (typeof FITNESS_PRESET_GROUPS)[number]

export const FITNESS_PRESETS = [
  'GYM',
  'BOUTIQUE_CROSSFIT',
  'YOGA',
  'DANCE',
  'MARTIAL_ARTS',
  'POOL',
  'SPORTS_ACADEMY',
  'TURF_COURT',
  'OPERATOR_GYM',
  'PHYSIO_FITNESS',
  'SENIOR_KIDS',
  'ONLINE_HYBRID',
] as const
export type FitnessPreset = (typeof FITNESS_PRESETS)[number]

export interface FitnessPresetMetadata {
  readonly key: FitnessPreset
  readonly group: FitnessPresetGroup
  readonly defaultLevel: 'BRANCH' | 'GROUND' | 'CLUBHOUSE'
  readonly kitSlug: string
}

export const FITNESS_PRESET_METADATA: Record<FitnessPreset, FitnessPresetMetadata> = {
  GYM: { key: 'GYM', group: 'GYMS_STUDIOS', defaultLevel: 'BRANCH', kitSlug: 'gym' },
  BOUTIQUE_CROSSFIT: { key: 'BOUTIQUE_CROSSFIT', group: 'GYMS_STUDIOS', defaultLevel: 'BRANCH', kitSlug: 'boutique-crossfit-studio' },
  YOGA: { key: 'YOGA', group: 'GYMS_STUDIOS', defaultLevel: 'BRANCH', kitSlug: 'yoga-studio-school' },
  DANCE: { key: 'DANCE', group: 'GYMS_STUDIOS', defaultLevel: 'BRANCH', kitSlug: 'dance-studio' },
  MARTIAL_ARTS: { key: 'MARTIAL_ARTS', group: 'GYMS_STUDIOS', defaultLevel: 'BRANCH', kitSlug: 'martial-arts' },
  POOL: { key: 'POOL', group: 'WATER_SPORT', defaultLevel: 'BRANCH', kitSlug: 'pool' },
  SPORTS_ACADEMY: { key: 'SPORTS_ACADEMY', group: 'WATER_SPORT', defaultLevel: 'GROUND', kitSlug: 'sports-academy' },
  TURF_COURT: { key: 'TURF_COURT', group: 'WATER_SPORT', defaultLevel: 'GROUND', kitSlug: 'turf-court-venue' },
  OPERATOR_GYM: { key: 'OPERATOR_GYM', group: 'PROGRAMMES', defaultLevel: 'CLUBHOUSE', kitSlug: 'operator-run-gym' },
  PHYSIO_FITNESS: { key: 'PHYSIO_FITNESS', group: 'PROGRAMMES', defaultLevel: 'BRANCH', kitSlug: 'physio-led-fitness' },
  SENIOR_KIDS: { key: 'SENIOR_KIDS', group: 'PROGRAMMES', defaultLevel: 'BRANCH', kitSlug: 'senior-kids-fitness' },
  ONLINE_HYBRID: { key: 'ONLINE_HYBRID', group: 'PROGRAMMES', defaultLevel: 'BRANCH', kitSlug: 'online-hybrid-coaching' },
}

export const FITNESS_MEMBER_MODES = ['FULL', 'BESIDE'] as const
export type FitnessMemberMode = (typeof FITNESS_MEMBER_MODES)[number]

export const FITNESS_BESIDE_SYSTEMS = [
  'FITNESSFORCE',
  'ZENOTI',
  'MINDBODY',
  'GLOFOX',
  'WODIFY',
  'PLAYO',
  'HUDLE',
  'OTHER',
] as const
export type FitnessBesideSystem = (typeof FITNESS_BESIDE_SYSTEMS)[number]

export const FITNESS_TIERS = ['BUDGET', 'MID', 'PREMIUM'] as const
export type FitnessTier = (typeof FITNESS_TIERS)[number]

export interface ClinicsPresetMetadata {
  readonly key: ClinicsPreset
  readonly family: ClinicsPresetFamily
  readonly defaultSiteKind: ClinicsSiteKind
  readonly kitSlug: string
}

export const CLINICS_PRESET_METADATA: Record<ClinicsPreset, ClinicsPresetMetadata> = {
  SINGLE_DOCTOR_FAMILY: {
    key: 'SINGLE_DOCTOR_FAMILY',
    family: 'CONSULT',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'single-doctor-family-clinic',
  },
  POLYCLINIC: {
    key: 'POLYCLINIC',
    family: 'CONSULT',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'polyclinic',
  },
  MATERNITY_CHILD: {
    key: 'MATERNITY_CHILD',
    family: 'CONSULT',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'maternity-child-clinic',
  },
  MENTAL_HEALTH: {
    key: 'MENTAL_HEALTH',
    family: 'CONSULT',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'mental-health-clinic',
  },
  AYUSH_WELLNESS: {
    key: 'AYUSH_WELLNESS',
    family: 'CONSULT',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'ayush-wellness',
  },
  VACCINATION: {
    key: 'VACCINATION',
    family: 'CONSULT',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'vaccination-centre',
  },
  DENTAL: {
    key: 'DENTAL',
    family: 'PROCEDURE',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'dental-clinic',
  },
  EYE_OPTICAL: {
    key: 'EYE_OPTICAL',
    family: 'PROCEDURE',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'eye-optical',
  },
  SKIN_COSMETIC_HAIR: {
    key: 'SKIN_COSMETIC_HAIR',
    family: 'PROCEDURE',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'skin-cosmetic-hair',
  },
  IVF: {
    key: 'IVF',
    family: 'PROCEDURE',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'ivf-centre',
  },
  PHYSIO_REHAB: {
    key: 'PHYSIO_REHAB',
    family: 'PROCEDURE',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'physio-rehab',
  },
  DIALYSIS: {
    key: 'DIALYSIS',
    family: 'PROCEDURE',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'dialysis-centre',
  },
  DAY_CARE_NURSING_HOME: {
    key: 'DAY_CARE_NURSING_HOME',
    family: 'PROCEDURE',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'day-care-nursing-home',
  },
  PATHOLOGY_LAB: {
    key: 'PATHOLOGY_LAB',
    family: 'LABS',
    defaultSiteKind: 'LAB',
    kitSlug: 'pathology-lab',
  },
  COLLECTION_CENTRE: {
    key: 'COLLECTION_CENTRE',
    family: 'LABS',
    defaultSiteKind: 'COLLECTION_CENTRE',
    kitSlug: 'collection-centre',
  },
  HOME_COLLECTION: {
    key: 'HOME_COLLECTION',
    family: 'LABS',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'home-collection',
  },
  IMAGING_CENTRE: {
    key: 'IMAGING_CENTRE',
    family: 'IMAGING',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'imaging-centre',
  },
  BLOOD_BANK: {
    key: 'BLOOD_BANK',
    family: 'BLOOD_BANK',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'blood-bank',
  },
  PHARMACY_CHAIN: {
    key: 'PHARMACY_CHAIN',
    family: 'PHARMACY',
    defaultSiteKind: 'PHARMACY',
    kitSlug: 'pharmacy-chain',
  },
  PHARMA_DEVICE_DISTRIBUTOR: {
    key: 'PHARMA_DEVICE_DISTRIBUTOR',
    family: 'PHARMACY',
    defaultSiteKind: 'WAREHOUSE',
    kitSlug: 'pharma-device-distributor',
  },
  VET_CLINIC: {
    key: 'VET_CLINIC',
    family: 'VET',
    defaultSiteKind: 'CLINIC',
    kitSlug: 'vet-clinic',
  },
}

export const STAYS_STARTER_DEPARTMENTS = [
  { key: 'HOUSEKEEPING', label: 'Housekeeping', archived: false },
  { key: 'FRONT_OFFICE', label: 'Front office', archived: false },
  { key: 'MAINTENANCE', label: 'Maintenance', archived: false },
] as const

export const STAYS_STARTER_DUTY_ROLES = [
  { key: 'room_attendant', label: 'Room attendant', sector: 'stays', department: 'HOUSEKEEPING' },
  { key: 'hk_supervisor', label: 'Housekeeping supervisor', sector: 'stays', department: 'HOUSEKEEPING' },
  { key: 'front_desk', label: 'Front desk', sector: 'stays', department: 'FRONT_OFFICE' },
  { key: 'runner', label: 'Runner', sector: 'stays', department: 'FRONT_OFFICE' },
  { key: 'maintenance_tech', label: 'Maintenance technician', sector: 'stays', department: 'MAINTENANCE' },
] as const

export const JOBS_STARTER_DEPARTMENTS = [
  { key: 'FIELD', label: 'Field', archived: false },
  { key: 'OFFICE', label: 'Office', archived: false },
] as const

export const JOBS_STARTER_DUTY_ROLES = [
  { key: 'technician', label: 'Technician', sector: 'jobs', department: 'FIELD' },
  { key: 'technician_helper', label: 'Technician helper', sector: 'jobs', department: 'FIELD' },
  { key: 'dispatcher', label: 'Dispatcher', sector: 'jobs', department: 'OFFICE' },
] as const

export const HOSPITAL_STARTER_DEPARTMENTS = [
  { key: 'EMERGENCY', label: 'Emergency', archived: false },
  { key: 'OPD', label: 'OPD', archived: false },
  { key: 'IPD_WARDS', label: 'Inpatient wards', archived: false },
  { key: 'ICU', label: 'ICU', archived: false },
  { key: 'OT', label: 'Operation theatre', archived: false },
  { key: 'LABOUR_ROOM', label: 'Labour room', archived: false },
  { key: 'RADIOLOGY', label: 'Radiology', archived: false },
  { key: 'LAB', label: 'Laboratory', archived: false },
  { key: 'PHARMACY_STORES', label: 'Pharmacy & stores', archived: false },
  { key: 'HOUSEKEEPING', label: 'Housekeeping', archived: false },
  { key: 'BIOMEDICAL', label: 'Biomedical engineering', archived: false },
  { key: 'FRONT_DESK', label: 'Front desk', archived: false },
  { key: 'ADMIN', label: 'Administration', archived: false },
] as const

export const HOSPITAL_STARTER_DUTY_ROLES = [
  { key: 'unit_in_charge', label: 'Unit in-charge', sector: 'hospital', department: 'IPD_WARDS' },
  { key: 'unit_backup', label: 'Unit backup', sector: 'hospital', department: 'IPD_WARDS' },
  { key: 'unit_on_call', label: 'Unit on-call', sector: 'hospital', department: 'IPD_WARDS' },
  { key: 'hk_supervisor', label: 'Housekeeping supervisor', sector: 'hospital', department: 'HOUSEKEEPING' },
  { key: 'housekeeping_staff', label: 'Housekeeping staff', sector: 'hospital', department: 'HOUSEKEEPING' },
  { key: 'biomedical_engineer', label: 'Biomedical engineer', sector: 'hospital', department: 'BIOMEDICAL' },
  { key: 'front_desk', label: 'Front desk', sector: 'hospital', department: 'FRONT_DESK' },
  { key: 'duty_manager', label: 'Duty manager', sector: 'hospital', department: 'ADMIN' },
] as const

export const PLANT_STARTER_DEPARTMENTS = [
  { key: 'PRODUCTION', label: 'Production', archived: false },
  { key: 'MAINTENANCE', label: 'Maintenance', archived: false },
  { key: 'QUALITY', label: 'Quality', archived: false },
  { key: 'SAFETY', label: 'Safety & compliance', archived: false },
  { key: 'STORES', label: 'Stores & materials', archived: false },
] as const

export const PLANT_STARTER_DUTY_ROLES = [
  { key: 'operator', label: 'Machine operator', sector: 'plant', department: 'PRODUCTION' },
  { key: 'line_supervisor', label: 'Line supervisor', sector: 'plant', department: 'PRODUCTION' },
  { key: 'shift_in_charge', label: 'Shift in-charge', sector: 'plant', department: 'PRODUCTION' },
  { key: 'maintenance_tech', label: 'Maintenance technician', sector: 'plant', department: 'MAINTENANCE' },
  { key: 'quality_inspector', label: 'Quality inspector', sector: 'plant', department: 'QUALITY' },
  { key: 'safety_officer', label: 'Safety officer', sector: 'plant', department: 'SAFETY' },
  { key: 'plant_manager', label: 'Plant manager', sector: 'plant', department: 'PRODUCTION' },
  { key: 'stores_in_charge', label: 'Stores in-charge', sector: 'plant', department: 'STORES' },
] as const

export const FITNESS_STARTER_DEPARTMENTS = [
  { key: 'FLOOR', label: 'Floor', archived: false },
  { key: 'POOL', label: 'Pool', archived: false },
  { key: 'FRONT_DESK', label: 'Front desk', archived: false },
  { key: 'COACHING', label: 'Coaching', archived: false },
] as const

export const FITNESS_STARTER_DUTY_ROLES = [
  { key: 'trainer', label: 'Trainer', sector: 'fitness', department: 'COACHING' },
  { key: 'head_coach', label: 'Head coach', sector: 'fitness', department: 'COACHING' },
  { key: 'lifeguard', label: 'Lifeguard', sector: 'fitness', department: 'POOL' },
  { key: 'front_desk', label: 'Front desk', sector: 'fitness', department: 'FRONT_DESK' },
  { key: 'housekeeping', label: 'Housekeeping', sector: 'fitness', department: 'FLOOR' },
  { key: 'night_guard', label: 'Night guard', sector: 'fitness', department: 'FLOOR' },
  { key: 'physio', label: 'Physiotherapist', sector: 'fitness', department: 'COACHING' },
] as const

export interface StaysPresetStarterSchedule {
  key: string
  title: string
  templateSlug: string
  targetLevel: 'ROOM' | 'AREA'
  cadence: { kind: 'DAILY' }
  window: { startMinute: number; durationMinutes: number }
  dutyRoleKey: string
}

export const STAYS_STARTER_SCHEDULES: StaysPresetStarterSchedule[] = [
  {
    key: 'daily_room_checklist',
    title: 'Daily room checklist',
    templateSlug: 'default',
    targetLevel: 'ROOM',
    cadence: { kind: 'DAILY' },
    window: { startMinute: 540, durationMinutes: 480 },
    dutyRoleKey: 'room_attendant',
  },
  {
    key: 'daily_public_area_checklist',
    title: 'Daily public-area checklist',
    templateSlug: 'public-area-daily-cleaning',
    targetLevel: 'AREA',
    cadence: { kind: 'DAILY' },
    window: { startMinute: 480, durationMinutes: 600 },
    dutyRoleKey: 'room_attendant',
  },
]

export type OpsPreset = SitesPreset | ShopsPreset | StaysPreset | ClinicsPreset | PlantPreset | FitnessPreset

export const SHOPS_ZONE_KINDS = [
  'FLOOR',
  'COUNTER',
  'GODOWN',
  'COLD_ROOM',
  'STATION',
] as const
export type ShopsZoneKind = (typeof SHOPS_ZONE_KINDS)[number]

export const STAYS_BLOCK_KINDS = [
  'FLOOR',
  'BLOCK',
  'WING',
  'VILLA',
] as const
export type StaysBlockKind = (typeof STAYS_BLOCK_KINDS)[number]

export const STAYS_AREA_KINDS = [
  'LOBBY',
  'CORRIDOR',
  'WASHROOM',
  'POOL',
  'GYM',
  'SPA',
  'KITCHEN',
  'LAUNDRY',
  'STORE',
  'GARDEN',
  'OTHER',
] as const
export type StaysAreaKind = (typeof STAYS_AREA_KINDS)[number]

export const STAYS_PLACE_WRITE_KEYS = [
  'level',
  'name',
  'code',
  'preset',
  'blockKind',
  'areaKind',
  'children',
  'roomTypes',
  'roomTypeId',
  'minibarCheckOnCheckout',
] as const
export type StaysPlaceWriteKey = (typeof STAYS_PLACE_WRITE_KEYS)[number]

export const JOBS_PLACE_WRITE_KEYS = [
  'level',
  'name',
  'code',
  'children',
  'customerId',
  'address',
  'geo',
  'accessNotes',
] as const
export type JobsPlaceWriteKey = (typeof JOBS_PLACE_WRITE_KEYS)[number]

export const CLINICS_PLACE_WRITE_KEYS = [
  'level',
  'name',
  'code',
  'preset',
  'siteKind',
  'children',
  'hubPlaceId',
] as const
export type ClinicsPlaceWriteKey = (typeof CLINICS_PLACE_WRITE_KEYS)[number]

export const FITNESS_PLACE_WRITE_KEYS = [
  'level',
  'name',
  'code',
  'preset',
  'memberMode',
  'besideSystem',
  'besideSystemName',
  'womenOnly',
  'open24h',
  'tier',
  'children',
] as const
export type FitnessPlaceWriteKey = (typeof FITNESS_PLACE_WRITE_KEYS)[number]

export type OpsPlaceLevelConstruction =
  | 'SITE'
  | 'BUILDING'
  | 'FLOOR'
  | 'ROOM'
  | 'CHAINAGE'

export type OpsPlaceLevelHospital =
  | 'FACILITY'
  | 'DEPARTMENT'
  | 'UNIT'
  | 'WARD'
  | 'ROOM'
  | 'BED'

export type OpsPlaceLevelSites =
  | 'CLIENT'
  | 'SITE'
  | 'POST'
  | 'AREA'
  | 'CHECKPOINT'

export type OpsPlaceLevelShops =
  | 'GROUP'
  | 'OUTLET'
  | 'ZONE'

export type OpsPlaceLevelStays =
  | 'GROUP'
  | 'PROPERTY'
  | 'BLOCK'
  | 'ROOM'
  | 'AREA'
  | 'BED'

export type OpsPlaceLevelJobs =
  | 'SERVICE_AREA'
  | 'SITE'

export type OpsPlaceLevelClinics =
  | 'ORG'
  | 'SITE'
  | 'FLOOR'
  | 'ZONE'
  | 'ROOM'
  | 'CHAIR'
  | 'BAY'

export type OpsPlaceLevelPlant =
  | 'PLANT'
  | 'LINE'
  | 'FLOOR'
  | 'MACHINE'

export type OpsPlaceLevelFitness =
  | 'ORG'
  | 'CLIENT'
  | 'BRANCH'
  | 'GROUND'
  | 'CLUBHOUSE'
  | 'FLOOR'
  | 'ZONE'
  | 'STUDIO'
  | 'POOL'
  | 'COURT'
  | 'LANE'
  | 'ROOM'

export type OpsPlaceLevel =
  | OpsPlaceLevelConstruction
  | OpsPlaceLevelHospital
  | OpsPlaceLevelSites
  | OpsPlaceLevelShops
  | OpsPlaceLevelStays
  | OpsPlaceLevelJobs
  | OpsPlaceLevelClinics
  | OpsPlaceLevelPlant
  | OpsPlaceLevelFitness

export const CONSTRUCTION_PLACE_LEVELS: readonly OpsPlaceLevelConstruction[] = [
  'SITE',
  'BUILDING',
  'FLOOR',
  'ROOM',
  'CHAINAGE',
] as const

export const HOSPITAL_PLACE_LEVELS: readonly OpsPlaceLevelHospital[] = [
  'FACILITY',
  'DEPARTMENT',
  'UNIT',
  'WARD',
  'ROOM',
  'BED',
] as const

export const SITES_PLACE_LEVELS: readonly OpsPlaceLevelSites[] = [
  'CLIENT',
  'SITE',
  'POST',
  'AREA',
  'CHECKPOINT',
] as const

export const SHOPS_PLACE_LEVELS: readonly OpsPlaceLevelShops[] = [
  'GROUP',
  'OUTLET',
  'ZONE',
] as const

export const STAYS_PLACE_LEVELS: readonly OpsPlaceLevelStays[] = [
  'GROUP',
  'PROPERTY',
  'BLOCK',
  'ROOM',
  'AREA',
  'BED',
] as const

export const JOBS_PLACE_LEVELS: readonly OpsPlaceLevelJobs[] = [
  'SERVICE_AREA',
  'SITE',
] as const

export const CLINICS_PLACE_LEVELS: readonly OpsPlaceLevelClinics[] = [
  'ORG',
  'SITE',
  'FLOOR',
  'ZONE',
  'ROOM',
  'CHAIR',
  'BAY',
] as const

export const PLANT_PLACE_LEVELS: readonly OpsPlaceLevelPlant[] = [
  'PLANT',
  'LINE',
  'FLOOR',
  'MACHINE',
] as const

export const FITNESS_PLACE_LEVELS: readonly OpsPlaceLevelFitness[] = [
  'ORG',
  'CLIENT',
  'BRANCH',
  'GROUND',
  'CLUBHOUSE',
  'FLOOR',
  'ZONE',
  'STUDIO',
  'POOL',
  'COURT',
  'LANE',
  'ROOM',
] as const

export interface OpsSectorDefinition {
  readonly key: OpsSector
  readonly moduleKey:
    | 'construction'
    | 'hospital'
    | 'sites'
    | 'delivery'
    | 'shops'
    | 'stays'
    | 'jobs'
    | 'clinics'
    | 'plant'
    | 'fitness'
  readonly routeSegment: string
  readonly view:
    | 'CONSTRUCTION'
    | 'HOSPITAL'
    | 'SITES'
    | 'DELIVERY'
    | 'SHOPS'
    | 'STAYS'
    | 'JOBS'
    | 'CLINICS'
    | 'PLANT'
    | 'FITNESS'
  readonly levels: readonly string[]
  readonly rootLevels: readonly string[]
  readonly allowedChildren: Readonly<Record<string, readonly string[]>>
  readonly defaultChild: Readonly<Record<string, string>>
  readonly allowsPatientLinked: boolean
  readonly requestsEnabled: boolean
  readonly assetsEnabled: boolean
  readonly bookingsEnabled: boolean
  readonly bookingsMultiResource?: boolean
  readonly anonymousBookingReminders?: boolean
  readonly sampleCustodyEnabled?: boolean
  readonly sampleStageMinutes?: Readonly<Record<string, number>>
  readonly queueHoldDefaults?: Readonly<{ DILATION: number; OBSERVATION: number }>
  readonly customersEnabled?: boolean
  readonly reliefOnAbsence?: boolean
  readonly staffSkillsEnabled?: boolean
  readonly onCallEnabled?: boolean
  readonly patientDirectoryAvailable?: boolean
  readonly presetLevel?: string
  readonly presetLevels?: readonly string[]
  readonly presets?: readonly OpsPreset[]
  readonly presetDefaultChild?: Readonly<Record<string, Readonly<Record<string, string>>>>
  readonly qrLeafLevel?: string
  readonly qrLevels?: readonly string[]
  readonly assetPlaceLevel?: string
  readonly oneAssetPerPlace?: boolean
  readonly bmwRequirePreTreatmentPhotoDefault?: boolean
}

export const OPS_SECTORS: Record<OpsSector, OpsSectorDefinition> = {
  construction: {
    key: 'construction',
    moduleKey: 'construction',
    routeSegment: 'construction',
    view: 'CONSTRUCTION',
    levels: CONSTRUCTION_PLACE_LEVELS,
    rootLevels: ['SITE', 'CHAINAGE'],
    allowedChildren: {
      SITE: ['BUILDING', 'FLOOR', 'CHAINAGE', 'ROOM'],
      BUILDING: ['FLOOR', 'ROOM'],
      FLOOR: ['ROOM'],
      CHAINAGE: ['ROOM'],
      ROOM: [],
    },
    defaultChild: {
      SITE: 'BUILDING',
      BUILDING: 'FLOOR',
      FLOOR: 'ROOM',
      CHAINAGE: 'ROOM',
    },
    allowsPatientLinked: false,
    requestsEnabled: true,
    assetsEnabled: true,
    bookingsEnabled: false,
  },
  hospital: {
    key: 'hospital',
    moduleKey: 'hospital',
    routeSegment: 'hospital',
    view: 'HOSPITAL',
    levels: HOSPITAL_PLACE_LEVELS,
    rootLevels: ['FACILITY'],
    allowedChildren: {
      FACILITY: ['DEPARTMENT', 'UNIT', 'WARD', 'ROOM', 'BED'],
      DEPARTMENT: ['UNIT', 'WARD', 'ROOM', 'BED'],
      UNIT: ['WARD', 'ROOM', 'BED'],
      WARD: ['ROOM', 'BED'],
      ROOM: ['BED'],
      BED: [],
    },
    defaultChild: {
      FACILITY: 'DEPARTMENT',
      DEPARTMENT: 'UNIT',
      UNIT: 'WARD',
      WARD: 'ROOM',
      ROOM: 'BED',
    },
    allowsPatientLinked: true,
    requestsEnabled: true,
    assetsEnabled: true,
    bookingsEnabled: true,
    staffSkillsEnabled: true,
    onCallEnabled: true,
    patientDirectoryAvailable: true,
    bmwRequirePreTreatmentPhotoDefault: false,
  },
  sites: {
    key: 'sites',
    moduleKey: 'sites',
    routeSegment: 'sites',
    view: 'SITES',
    levels: SITES_PLACE_LEVELS,
    rootLevels: ['CLIENT'],
    allowedChildren: {
      CLIENT: ['SITE'],
      SITE: ['POST', 'AREA', 'CHECKPOINT'],
      POST: ['CHECKPOINT'],
      AREA: ['CHECKPOINT'],
      CHECKPOINT: [],
    },
    defaultChild: {
      CLIENT: 'SITE',
      SITE: 'POST',
      POST: 'CHECKPOINT',
      AREA: 'CHECKPOINT',
    },
    allowsPatientLinked: false,
    requestsEnabled: true,
    assetsEnabled: false,
    bookingsEnabled: true,
    reliefOnAbsence: true,
    presetLevel: 'SITE',
    presets: SITES_PRESETS,
    presetDefaultChild: {
      GUARDING: { SITE: 'POST' },
      FACILITY: { SITE: 'AREA' },
    },
    qrLeafLevel: 'CHECKPOINT',
    qrLevels: ['CHECKPOINT'],
  },
  delivery: {
    key: 'delivery',
    moduleKey: 'delivery',
    routeSegment: 'delivery',
    view: 'DELIVERY',
    levels: [],
    rootLevels: [],
    allowedChildren: {},
    defaultChild: {},
    allowsPatientLinked: false,
    requestsEnabled: true,
    assetsEnabled: false,
    bookingsEnabled: false,
  },
  shops: {
    key: 'shops',
    moduleKey: 'shops',
    routeSegment: 'shops',
    view: 'SHOPS',
    levels: SHOPS_PLACE_LEVELS,
    rootLevels: ['GROUP'],
    allowedChildren: {
      GROUP: ['OUTLET'],
      OUTLET: ['ZONE'],
      ZONE: [],
    },
    defaultChild: {
      GROUP: 'OUTLET',
      OUTLET: 'ZONE',
    },
    allowsPatientLinked: false,
    requestsEnabled: true,
    assetsEnabled: true,
    bookingsEnabled: false,
    qrLeafLevel: 'OUTLET',
    qrLevels: ['OUTLET'],
    presetLevel: 'OUTLET',
    presets: SHOPS_PRESETS,
  },
  stays: {
    key: 'stays',
    moduleKey: 'stays',
    routeSegment: 'stays',
    view: 'STAYS',
    levels: STAYS_PLACE_LEVELS,
    rootLevels: ['GROUP'],
    allowedChildren: {
      GROUP: ['PROPERTY'],
      PROPERTY: ['BLOCK', 'ROOM', 'AREA'],
      BLOCK: ['ROOM', 'AREA'],
      ROOM: ['BED'],
      AREA: [],
      BED: [],
    },
    defaultChild: {
      GROUP: 'PROPERTY',
      PROPERTY: 'BLOCK',
      BLOCK: 'ROOM',
      ROOM: 'BED',
    },
    allowsPatientLinked: false,
    requestsEnabled: true,
    assetsEnabled: true,
    bookingsEnabled: true,
    qrLevels: ['ROOM', 'AREA', 'BED'],
    presetLevel: 'PROPERTY',
    presets: STAYS_PRESETS,
  },
  jobs: {
    key: 'jobs',
    moduleKey: 'jobs',
    routeSegment: 'jobs',
    view: 'JOBS',
    levels: JOBS_PLACE_LEVELS,
    rootLevels: ['SERVICE_AREA', 'SITE'],
    allowedChildren: {
      SERVICE_AREA: ['SITE'],
      SITE: [],
    },
    defaultChild: {
      SERVICE_AREA: 'SITE',
    },
    allowsPatientLinked: false,
    requestsEnabled: true,
    assetsEnabled: true,
    bookingsEnabled: false,
    customersEnabled: true,
  },
  clinics: {
    key: 'clinics',
    moduleKey: 'clinics',
    routeSegment: 'clinics',
    view: 'CLINICS',
    levels: CLINICS_PLACE_LEVELS,
    rootLevels: ['ORG'],
    allowedChildren: {
      ORG: ['SITE'],
      SITE: ['FLOOR', 'ZONE', 'ROOM', 'CHAIR', 'BAY'],
      FLOOR: ['ZONE', 'ROOM', 'CHAIR', 'BAY'],
      ZONE: ['ROOM', 'CHAIR', 'BAY'],
      ROOM: ['CHAIR', 'BAY'],
      CHAIR: [],
      BAY: [],
    },
    defaultChild: {
      ORG: 'SITE',
      SITE: 'FLOOR',
      FLOOR: 'ROOM',
      ZONE: 'ROOM',
      ROOM: 'CHAIR',
    },
    allowsPatientLinked: false,
    requestsEnabled: true,
    assetsEnabled: true,
    bookingsEnabled: true,
    qrLevels: ['ROOM', 'CHAIR', 'BAY'],
    presetLevel: 'SITE',
    presets: CLINICS_PRESETS,
    presetDefaultChild: {
      COLLECTION_CENTRE: { SITE: 'ROOM' },
      HOME_COLLECTION: { SITE: 'ROOM' },
      DENTAL: { ROOM: 'CHAIR' },
      DIALYSIS: { ROOM: 'CHAIR' },
      DAY_CARE_NURSING_HOME: { ROOM: 'BAY' },
    },
    bmwRequirePreTreatmentPhotoDefault: true,
    bookingsMultiResource: true,
    anonymousBookingReminders: true,
    sampleCustodyEnabled: true,
    sampleStageMinutes: {
      COLLECTED: 60,
      AT_CENTRE: 120,
      WITH_RUNNER: 180,
      AT_LAB: 240,
    },
    queueHoldDefaults: {
      DILATION: 30,
      OBSERVATION: 15,
    },
  },
  plant: {
    key: 'plant',
    moduleKey: 'plant',
    routeSegment: 'plant',
    view: 'PLANT',
    levels: PLANT_PLACE_LEVELS,
    rootLevels: ['PLANT'],
    allowedChildren: {
      PLANT: ['LINE', 'FLOOR', 'MACHINE'],
      FLOOR: ['LINE', 'MACHINE'],
      LINE: ['MACHINE'],
      MACHINE: [],
    },
    defaultChild: {
      PLANT: 'LINE',
      FLOOR: 'LINE',
      LINE: 'MACHINE',
    },
    allowsPatientLinked: false,
    requestsEnabled: true,
    assetsEnabled: true,
    bookingsEnabled: false,
    qrLevels: ['LINE', 'FLOOR', 'MACHINE'],
    presetLevel: 'PLANT',
    presets: PLANT_PRESETS,
    assetPlaceLevel: 'MACHINE',
    oneAssetPerPlace: true,
  },
  fitness: {
    key: 'fitness',
    moduleKey: 'fitness',
    routeSegment: 'fitness',
    view: 'FITNESS',
    levels: FITNESS_PLACE_LEVELS,
    rootLevels: ['ORG'],
    allowedChildren: {
      ORG: ['BRANCH', 'GROUND', 'CLUBHOUSE', 'CLIENT'],
      CLIENT: ['BRANCH', 'CLUBHOUSE'],
      BRANCH: ['FLOOR', 'ZONE', 'STUDIO', 'POOL', 'COURT', 'ROOM'],
      CLUBHOUSE: ['FLOOR', 'ZONE', 'STUDIO', 'POOL', 'COURT', 'ROOM'],
      GROUND: ['ZONE', 'COURT', 'LANE', 'ROOM'],
      FLOOR: ['ZONE', 'STUDIO', 'POOL', 'COURT', 'ROOM'],
      ZONE: ['STUDIO', 'POOL', 'COURT', 'LANE', 'ROOM'],
      POOL: ['LANE'],
      STUDIO: [],
      COURT: [],
      LANE: [],
      ROOM: [],
    },
    defaultChild: {
      ORG: 'BRANCH',
      CLIENT: 'CLUBHOUSE',
      BRANCH: 'FLOOR',
      CLUBHOUSE: 'ROOM',
      GROUND: 'COURT',
      FLOOR: 'STUDIO',
      ZONE: 'STUDIO',
      POOL: 'LANE',
    },
    allowsPatientLinked: false,
    requestsEnabled: true,
    assetsEnabled: true,
    bookingsEnabled: true,
    customersEnabled: true,
    presetLevels: ['BRANCH', 'GROUND', 'CLUBHOUSE'],
    presets: FITNESS_PRESETS,
    presetDefaultChild: {
      POOL: { BRANCH: 'POOL' },
      TURF_COURT: { GROUND: 'COURT' },
      SPORTS_ACADEMY: { GROUND: 'COURT' },
      ONLINE_HYBRID: { BRANCH: 'ROOM' },
      YOGA: { BRANCH: 'STUDIO' },
      DANCE: { BRANCH: 'STUDIO' },
      MARTIAL_ARTS: { BRANCH: 'STUDIO' },
      BOUTIQUE_CROSSFIT: { BRANCH: 'STUDIO' },
    },
    qrLevels: [
      'BRANCH',
      'GROUND',
      'CLUBHOUSE',
      'STUDIO',
      'POOL',
      'COURT',
      'LANE',
      'ROOM',
    ],
  },
}

export function isOpsSector(x: unknown): x is OpsSector {
  return typeof x === 'string' && (OPS_SECTOR_KEYS as readonly string[]).includes(x)
}

export function isRequestSector(x: unknown): x is OpsSector {
  return isOpsSector(x) && Boolean(OPS_SECTORS[x].requestsEnabled)
}

export function isAssetSector(x: unknown): x is OpsSector {
  return isOpsSector(x) && Boolean(OPS_SECTORS[x].assetsEnabled)
}

export function isBookingSector(x: unknown): x is OpsSector {
  return isOpsSector(x) && Boolean(OPS_SECTORS[x].bookingsEnabled)
}

export function isMultiResourceBookingSector(x: unknown): boolean {
  return isOpsSector(x) && Boolean(OPS_SECTORS[x].bookingsMultiResource)
}

export function isSampleCustodySector(x: unknown): boolean {
  return isOpsSector(x) && Boolean(OPS_SECTORS[x].sampleCustodyEnabled)
}

export function isAnonymousBookingReminderSector(x: unknown): boolean {
  return isOpsSector(x) && Boolean(OPS_SECTORS[x].anonymousBookingReminders)
}

export function isCustomerSector(x: unknown): x is OpsSector {
  return isOpsSector(x) && Boolean(OPS_SECTORS[x].bookingsEnabled || OPS_SECTORS[x].customersEnabled)
}

export function sectorSupportsReliefOnAbsence(x: unknown): boolean {
  return isOpsSector(x) && Boolean(OPS_SECTORS[x].reliefOnAbsence)
}

export function findOpsSectorByRouteSegment(segment: string): OpsSector | undefined {
  return OPS_SECTOR_KEYS.find((s) => OPS_SECTORS[s].routeSegment === segment)
}

export function findOpsSectorByView(view: string): OpsSector | undefined {
  return OPS_SECTOR_KEYS.find((s) => OPS_SECTORS[s].view === view)
}

export function sectorRootLevels(sector: OpsSector): readonly string[] {
  return OPS_SECTORS[sector]?.rootLevels ?? []
}

export function isValidChildLevel(
  sector: OpsSector,
  parentLevel: string | null,
  childLevel: string,
): boolean {
  if (!parentLevel) {
    const rootLevels = sectorRootLevels(sector)
    return rootLevels.includes(childLevel)
  }

  const def = OPS_SECTORS[sector]
  if (!def) return false
  const allowed = def.allowedChildren[parentLevel] ?? []
  return allowed.includes(childLevel)
}

export function sectorAllowsPresetOnLevel(sector: OpsSector, level: string): boolean {
  const def = OPS_SECTORS[sector]
  if (!def || !def.presets || !def.presets.length) return false
  if (def.presetLevels && def.presetLevels.length > 0) {
    return def.presetLevels.includes(level)
  }
  return def.presetLevel === level
}

export function defaultChildLevel(
  sector: OpsSector,
  parentLevel: string | null,
  preset?: string | null,
): string | undefined {
  const def = OPS_SECTORS[sector]
  if (!def) return undefined

  if (!parentLevel) {
    return def.rootLevels[0]
  }

  if (preset && def.presetDefaultChild && def.presetDefaultChild[preset]) {
    const override = def.presetDefaultChild[preset][parentLevel]
    if (override) return override
  }

  return def.defaultChild[parentLevel]
}

export function isHospitalSector(x: unknown): boolean {
  return x === 'hospital'
}

export function isClinicsSector(x: unknown): boolean {
  return x === 'clinics'
}

export function isPlantSector(x: unknown): boolean {
  return x === 'plant'
}

export function isFitnessSector(x: unknown): boolean {
  return x === 'fitness'
}

export function sectorBmwRequirePreTreatmentPhotoDefault(x: unknown): boolean {
  if (typeof x !== 'string' || !isOpsSector(x)) return false
  return Boolean(OPS_SECTORS[x].bmwRequirePreTreatmentPhotoDefault)
}


